import { useState } from "react";
import { Inbox, LayoutGrid, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { Hint } from "@/client/components/common/hint";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/client/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/client/components/ui/tooltip";
import {
  openPlacementReview,
  usePlacementState,
  useRequestPlacements,
} from "@/client/hooks/use-placement-suggestions";
import { useStoryData } from "@/client/hooks/use-story-data";
import { useTabActions } from "@/client/hooks/use-tabs-context";
import { CaptureBox } from "./capture-box";
import { FragmentCard } from "./fragment-card";

type Filter = "unplaced" | "all" | "archived";

const EMPTY_TEXT: Record<Filter, string> = {
  unplaced: "Every idea is used somewhere.",
  all: "Ideas you capture land here. Press Alt+N anywhere to add one.",
  archived: "Nothing archived.",
};

/** The navigator's Ideas: capture them and find them a place in the story. */
export function InboxPanel({ storyId }: { storyId: string }) {
  const [filter, setFilter] = useState<Filter>("unplaced");
  const { outline, fragments: index } = useStoryData();
  const { openBoardTab } = useTabActions();
  const { pendingAll, byFragment } = usePlacementState(storyId);
  const requestPlacements = useRequestPlacements(storyId);

  const rows = (filter === "unplaced" ? index.unplaced : filter === "archived" ? index.archived : index.active)
    .slice()
    .reverse();
  const canSuggest = index.unplaced.length > 0 && outline.chapters.length > 0;
  const filters: { value: Filter; label: string; count: number }[] = [
    { value: "unplaced", label: "Not used yet", count: index.unplaced.length },
    { value: "all", label: "All", count: index.active.length },
    { value: "archived", label: "Archived", count: index.archived.length },
  ];

  return (
    <div className="flex h-full flex-col">
      <div data-region="panel-header" className="flex items-center justify-between gap-2 border-b p-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          Ideas
          {index.unplaced.length > 0 && (
            <Hint
              title={`${index.unplaced.length} not used yet`}
              description="Ideas that aren't used in any scene or chapter yet. Drag one onto a scene, use the pin, or let the AI find places for them."
              side="bottom"
            >
              <span className="cursor-help rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary">
                {index.unplaced.length}
              </span>
            </Hint>
          )}
        </h2>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                disabled={!canSuggest || pendingAll}
                onClick={() => requestPlacements()}
                aria-label="Find places for unused ideas"
              >
                {pendingAll ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {outline.chapters.length === 0
                ? "Add a chapter first, or find chapters in your ideas on the board"
                : "Find places for unused ideas"}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => openBoardTab(storyId)}
                aria-label="Open the story board"
              >
                <LayoutGrid className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Story board</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="space-y-3 border-b p-3">
        <CaptureBox storyId={storyId} />
        {byFragment.size > 0 && (
          <Button size="sm" variant="secondary" className="w-full" onClick={() => openPlacementReview()}>
            <Sparkles />
            Review suggested places for {byFragment.size} {byFragment.size === 1 ? "idea" : "ideas"}
          </Button>
        )}
        <Select value={filter} onValueChange={(value) => setFilter(value as Filter)}>
          <SelectTrigger aria-label="Filter ideas" data-region="inbox-filters" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            {filters.map(({ value, label, count }) => (
              <SelectItem
                key={value}
                value={value}
                textValue={label}
                aria-label={`${label}, ${count} ${count === 1 ? "idea" : "ideas"}`}
                className="py-2.5"
              >
                <span>{label}</span>
                <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-muted-foreground">
            <Inbox className="size-8 text-muted-foreground/40" />
            {index.all.length === 0 ? EMPTY_TEXT.all : EMPTY_TEXT[filter]}
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {rows.map((fragment) => (
              <FragmentCard key={fragment.id} storyId={storyId} fragment={fragment} />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
